import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import ApiKeyService from "../../services/settings/ApiKeyService";
import ButtonLoading from "../../components/Loder/buttonLoading";

function ApiKeySettings() {
  const [apiKeys, setApiKeys]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking]   = useState(null);
  const [keyName, setKeyName]     = useState("");
  const [newKey, setNewKey]       = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied]       = useState(false);

  const companyId = parseInt(localStorage.getItem("active_company_id"), 10);

  const toastOpts = { position: "top-right", autoClose: 1500, hideProgressBar: false, pauseOnHover: true, draggable: true, theme: "light" };

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = () => {
    setLoading(true);
    ApiKeyService.listApiKeys()
      .then(res => { if (res.data.success === 1) setApiKeys(res.data.results); })
      .catch(() => toast.error("Failed to load API keys.", toastOpts))
      .finally(() => setLoading(false));
  };

  const handleGenerate = () => {
    if (!companyId) { toast.error("No active company found.", toastOpts); return; }
    setGenerating(true);
    ApiKeyService.generateApiKey(companyId, keyName.trim() || "Default Key")
      .then(res => {
        if (res.data.success === 1) {
          setNewKey(res.data.api_key);
          setShowModal(true);
          setKeyName("");
          fetchKeys();
        } else {
          toast.error(res.data.error || "Failed to generate key.", toastOpts);
        }
      })
      .catch(err => toast.error(err.response?.data?.error || "Failed to generate key.", toastOpts))
      .finally(() => setGenerating(false));
  };

  const handleRevoke = (pk, name) => {
    if (!window.confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
    setRevoking(pk);
    ApiKeyService.revokeApiKey(pk)
      .then(res => {
        if (res.data.success === 1) {
          toast.success(res.data.message || "API key revoked.", toastOpts);
          fetchKeys();
        }
      })
      .catch(() => toast.error("Failed to revoke API key.", toastOpts))
      .finally(() => setRevoking(null));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const closeModal = () => { setShowModal(false); setNewKey(null); setCopied(false); };

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : "Never";

  return (
    <>
      <ToastContainer />

      <div className="container-fluid p-4" style={{ backgroundColor: "#F0F2F5", minHeight: "100vh" }}>
        <h5 className="mb-4 fw-bold" style={{ color: "#44566C" }}>API Key Management</h5>

        {/* ── Generate ── */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "8px" }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1" style={{ color: "#44566C" }}>Generate New API Key</h6>
            <p className="text-muted small mb-3">Keys grant programmatic access. Keep them secret.</p>
            <div className="row align-items-end g-3">
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Key Name</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="e.g. Production Integration"
                  value={keyName}
                  onChange={e => setKeyName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleGenerate()}
                />
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-primary btn-gradient-primary w-100"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating
                    ? <ButtonLoading />
                    : <><i className="fa fa-key me-2" />Generate Key</>
                  }
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Key list ── */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3" style={{ color: "#44566C" }}>
              Your API Keys
              {!loading && <span className="ms-2 badge bg-secondary" style={{ fontSize: "12px" }}>{apiKeys.length}</span>}
            </h6>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <p className="mt-2 text-muted small">Loading keys…</p>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-5">
                <i className="fa fa-key fa-2x text-muted mb-3" style={{ display: "block" }} />
                <p className="text-muted mb-0">No API keys yet. Generate one above.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      {["Name", "Key Prefix", "Company", "Status", "Created", "Last Used", ""].map(h => (
                        <th key={h} className="small fw-semibold text-muted border-0 py-2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map(k => (
                      <tr key={k.id}>
                        <td className="fw-medium">{k.name || "—"}</td>
                        <td>
                          <code style={{ backgroundColor: "#F0F2F5", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
                            {k.prefix}••••••••••••
                          </code>
                        </td>
                        <td className="small text-muted">{k.company}</td>
                        <td>
                          <span className={`badge ${k.is_active ? "bg-success" : "bg-secondary"}`} style={{ fontSize: "11px" }}>
                            {k.is_active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className="small text-muted">{fmt(k.created_at)}</td>
                        <td className="small text-muted">{fmt(k.last_used_at)}</td>
                        <td>
                          {k.is_active && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleRevoke(k.id, k.name)}
                              disabled={revoking === k.id}
                            >
                              {revoking === k.id ? "Revoking…" : "Revoke"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── New-key modal ── */}
      {showModal && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center"
          }}
        >
          <div style={{
            backgroundColor: "#fff", borderRadius: "12px", padding: "36px",
            maxWidth: "520px", width: "92%", boxShadow: "0 24px 64px rgba(0,0,0,0.25)"
          }}>
            {/* Icon + heading */}
            <div className="text-center mb-4">
              <div style={{
                width: "60px", height: "60px", borderRadius: "50%",
                backgroundColor: "#FFF3CD", margin: "0 auto 14px",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <i className="fa fa-key" style={{ fontSize: "26px", color: "#F59E0B" }} />
              </div>
              <h5 className="fw-bold mb-1">API Key Generated!</h5>
              <p className="text-muted small mb-0">
                Copy and store this key somewhere safe.<br />
                <strong>It will not be shown again.</strong>
              </p>
            </div>

            {/* Warning banner */}
            <div style={{
              backgroundColor: "#FFF8E1", border: "1px solid #FFD54F",
              borderRadius: "8px", padding: "10px 14px", marginBottom: "20px"
            }}>
              <i className="fa fa-exclamation-triangle me-2" style={{ color: "#F9A825" }} />
              <small style={{ color: "#6D4C00" }}>
                If you lose this key you'll need to revoke it and generate a new one.
              </small>
            </div>

            {/* Key display + copy */}
            <label className="small fw-semibold text-muted mb-2" style={{ display: "block" }}>Your API Key</label>
            <div className="d-flex gap-2 mb-4">
              <input
                className="form-control"
                type="text"
                value={newKey}
                readOnly
                style={{ fontFamily: "monospace", fontSize: "13px", backgroundColor: "#F8FAFC", letterSpacing: "0.4px" }}
              />
              <button
                className={`btn ${copied ? "btn-success" : "btn-outline-primary"}`}
                onClick={handleCopy}
                style={{ minWidth: "90px", whiteSpace: "nowrap" }}
              >
                {copied
                  ? <><i className="fa fa-check me-1" />Copied</>
                  : <><i className="fa fa-copy me-1" />Copy</>
                }
              </button>
            </div>

            <button className="btn btn-secondary w-100" onClick={closeModal}>
              I've saved my key — Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ApiKeySettings;
